/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ShieldAlert, Download, Trash2, Loader2, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { requestGdprExport, deleteAccount, cancelDeleteAccount } from '@/lib/sprint5';

const GdprSettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const doExport = async () => {
    setDownloading(true);
    try {
      const blob = await requestGdprExport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sanitanexus_gdpr_export_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export ZIP scaricato');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore export');
    } finally { setDownloading(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount(confirmEmail.trim(), reason.trim() || undefined);
      toast.success('Account disattivato. Eliminazione definitiva tra 30 giorni.');
      setDialogOpen(false);
      setTimeout(async () => {
        try {
          await logout?.();
        } catch (logoutErr) {
          // Logout failed silently — user is already deactivated, session cookie may already be gone.
          if (process.env.NODE_ENV !== 'production') {
             
            console.debug('[GdprSettings] post-delete logout failed', logoutErr);
          }
        }
        navigate('/', { replace: true });
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await cancelDeleteAccount();
      toast.success('Richiesta di eliminazione annullata');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally { setBusy(false); }
  };

  const pendingDelete = user?.account_deletion_requested;

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6" data-testid="gdpr-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-primary" /> I miei dati (GDPR)
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Ai sensi degli articoli 15 e 17 del GDPR, puoi in qualsiasi momento scaricare i tuoi dati o chiedere la cancellazione dell'account.
        </p>
      </div>

      <Card><CardContent className="p-4 space-y-3">
        <div className="font-semibold text-sm">Esporta i miei dati</div>
        <p className="text-xs text-muted-foreground">
          Scarica un archivio ZIP con tutti i dati che abbiamo su di te (profilo, appuntamenti, prescrizioni, misurazioni IoT, note cliniche, wound cases, care team, recensioni, post pubblicati).
        </p>
        <Button onClick={doExport} disabled={downloading} data-testid="gdpr-export-btn">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Scarica archivio ZIP
        </Button>
      </CardContent></Card>

      {pendingDelete ? (
        <Card className="border-amber-300 bg-amber-50"><CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Richiesta di eliminazione attiva</div>
          <p className="text-xs text-amber-900">Il tuo account sarà eliminato definitivamente il <b>{new Date(user.account_deletion_scheduled_at).toLocaleDateString('it')}</b>. Puoi annullare finché sei ancora nel periodo di grazia (30 giorni).</p>
          <Button variant="outline" onClick={doCancel} disabled={busy} data-testid="gdpr-cancel-delete-btn">Annulla richiesta di eliminazione</Button>
        </CardContent></Card>
      ) : (
        <Card className="border-destructive/30"><CardContent className="p-4 space-y-3">
          <div className="font-semibold text-sm text-destructive">Elimina il mio account</div>
          <p className="text-xs text-muted-foreground">
            L'account viene disattivato subito. Dopo <b>30 giorni</b> tutti i tuoi dati personali vengono cancellati definitivamente (fatture legali conservate per legge fiscale). Puoi annullare la richiesta accedendo nuovamente entro la scadenza.
          </p>
          <Button variant="destructive" onClick={() => setDialogOpen(true)} data-testid="gdpr-delete-open-btn">
            <Trash2 className="h-4 w-4" /> Elimina account
          </Button>
        </CardContent></Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="gdpr-delete-dialog">
          <DialogHeader>
            <DialogTitle>Conferma eliminazione account</DialogTitle>
            <DialogDescription>Scrivi la tua email <b>{user?.email}</b> per confermare. Questa azione è <b>reversibile solo entro 30 giorni</b>.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email di conferma</Label>
              <Input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={user?.email} data-testid="gdpr-confirm-email-input" />
            </div>
            <div>
              <Label>Motivo (opzionale)</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="gdpr-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button variant="destructive" onClick={doDelete} disabled={busy || confirmEmail.trim().toLowerCase() !== (user?.email || '').toLowerCase()} data-testid="gdpr-confirm-delete-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GdprSettingsPage;
